import React from 'react';
import { DeleteOutlined, Email } from '@mui/icons-material';
import { Box, Card, CardContent, CardHeader, Divider, Grid, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { useEmailStatement } from 'api/useEmailStatement';
import { IconButton } from 'components/Elements/Buttons/IconButton';
import { NoRowsOverlay } from 'components/Elements/Buttons/NoRowsOverlay';
import { ParticipateToggleButton } from 'components/Elements/Buttons/ParticipateToggleButton';
import { EmailStatementDialog } from 'components/Elements/Dialogs/EmailStatementDialog';
import { Toast } from 'components/Elements/Feedback/Toast';
import { NumberInput } from 'components/Elements/Inputs/NumberInput';
import { formatProviderName } from 'utils/formatProviderName';
import { canEditPaymentAmount, formatToUSD, isValidPaymentAmount, NON_PARTICIPATING_FEE } from 'utils/participation';
import { useAssessmentRowActions } from 'utils/useAssessmentRowActions';
import { MaskedEmailField } from './MaskedEmailField';
import { PaymentItem } from './PhysicianSearchGrid';

export interface PhysicianSearchCardsProps {
  rows: Array<PaymentItem & { isNew?: boolean }>;
  setRows?: React.Dispatch<React.SetStateAction<Array<PaymentItem & { isNew?: boolean }>>>;
  handleDeleteClick?: (id: any) => void;
  handleMailClick?: (id: any) => void;
  disableMail?: boolean;
  onEditEmail?: (row: PaymentItem) => void;
  sx?: SxProps<Theme>;
}

interface PhysicianSearchCardProps {
  row: PaymentItem & { isNew?: boolean };
  isStatementEnabled: boolean;
  isEmailLoading: boolean;
  disableMail?: boolean;
  onEmailClick: (id: string, name: string) => void;
  onEditEmail?: (row: PaymentItem) => void;
  onDeleteClick?: (id: string) => void;
  onAmountChange: (id: string, amount: string) => void;
  onParticipateToggle: (id: string) => void;
}

const PhysicianSearchCard: React.FC<PhysicianSearchCardProps> = ({
  row,
  isStatementEnabled,
  isEmailLoading,
  disableMail,
  onEmailClick,
  onEditEmail,
  onDeleteClick,
  onAmountChange,
  onParticipateToggle,
}) => {
  const displayName = formatProviderName(row.firstName, row.lastName);

  return (
    <Card sx={{ boxShadow: 1, marginBottom: 2, border: '1px solid', borderColor: 'grey.300' }}>
      <CardHeader
        title={displayName}
        subheader={`${row.license}`}
        action={
          <>
            {isStatementEnabled && (
              <Tooltip title="Email me a copy of my statement" arrow>
                <span>
                  <IconButton
                    disabled={disableMail || isEmailLoading}
                    color="primary"
                    onClick={() => onEmailClick(row.id, displayName)}
                    aria-label={`Email statement for ${displayName}`}
                  >
                    <Email />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            <IconButton onClick={() => onDeleteClick?.(row.id)} aria-label={`Remove ${displayName}`} sx={{ color: 'dustyRose.main' }}>
              <DeleteOutlined />
            </IconButton>
          </>
        }
        sx={{ paddingBottom: 1 }}
      />
      <Divider />
      <CardContent>
        <Grid container justifyContent="space-between" alignItems="center" spacing={1}>
          <Grid container item xs={12} alignItems="center">
            <Grid item xs={4}>
              <Typography>Email</Typography>
            </Grid>
            <Grid item xs={8} sx={{ minWidth: 0 }}>
              <MaskedEmailField align="right" maskedEmail={row.maskedEmail} name={displayName} onEdit={() => onEditEmail?.(row)} />
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <Typography>Balance Due</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography textAlign="right" pr={1.8}>
                {formatToUSD(row.balance)}
              </Typography>
            </Grid>
          </Grid>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <Typography pt={1}>Payment Amount</Typography>
            </Grid>
            <Grid item xs={6}>
              {canEditPaymentAmount(row) ? (
                <Box display="flex" alignItems="flex-start" justifyContent="flex-end">
                  <Typography textAlign="right" padding={1}>
                    $
                  </Typography>
                  <NumberInput
                    placeholder={'Enter Amount...'}
                    allowNegative={false}
                    decimalScale={2}
                    fixedDecimalScale
                    color="primary"
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={row.amount}
                    InputProps={{
                      inputProps: {
                        style: { textAlign: 'right' },
                      },
                    }}
                    error={!isValidPaymentAmount(row.amount, row)}
                    helperText={
                      !isValidPaymentAmount(row.amount, row) ? `Amount must be greater than ${formatToUSD(NON_PARTICIPATING_FEE)}` : ''
                    }
                    onChange={event => {
                      let newValue = event.target.value;

                      if (Number.isNaN(newValue) || parseFloat(newValue) <= 0) {
                        newValue = '';
                      }

                      onAmountChange(row.id, newValue);
                    }}
                  />
                </Box>
              ) : (
                <Typography textAlign="right" pr={1.8} pt={1}>
                  {formatToUSD(row.amount)}
                </Typography>
              )}
            </Grid>
          </Grid>
          <Grid item xs={12} mt={1}>
            <ParticipateToggleButton
              participating={row.participating}
              locked={row.participationLocked}
              onToggle={() => onParticipateToggle(row.id)}
              fullWidth
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export const PhysicianSearchCards: React.FC<PhysicianSearchCardsProps> = ({
  rows,
  setRows,
  handleDeleteClick,
  handleMailClick,
  disableMail,
  onEditEmail,
  sx,
}) => {
  const {
    isStatementEnabled,
    isLoading,
    toastOpen,
    toastMessage,
    toastSeverity,
    emailDialogOpen,
    emailRecipient,
    openEmailDialog,
    handleEmailConfirm,
    handleEmailDialogClose,
    handleToastClose,
  } = useEmailStatement({ onSent: id => handleMailClick?.(id) });
  const { handleAmountChange, handleParticipateToggle } = useAssessmentRowActions(setRows);

  return (
    <Box sx={sx}>
      {rows.length === 0 && <NoRowsOverlay />}
      {rows.map(row => (
        <PhysicianSearchCard
          key={row.id}
          row={row}
          isStatementEnabled={isStatementEnabled}
          isEmailLoading={isLoading}
          disableMail={disableMail}
          onEmailClick={openEmailDialog}
          onEditEmail={onEditEmail}
          onDeleteClick={handleDeleteClick}
          onAmountChange={handleAmountChange}
          onParticipateToggle={handleParticipateToggle}
        />
      ))}
      {isStatementEnabled && (
        <>
          <Toast open={toastOpen} message={toastMessage} severity={toastSeverity} onClose={handleToastClose} />
          <EmailStatementDialog
            open={emailDialogOpen}
            onClose={handleEmailDialogClose}
            onConfirm={handleEmailConfirm}
            loading={isLoading}
            physicianName={emailRecipient?.name}
          />
        </>
      )}
    </Box>
  );
};
